const DAY_MS = 86_400_000

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const pad = (value: number): string => String(value).padStart(2, '0')

export const isDayKey = (value: string): boolean => DAY_KEY_PATTERN.test(value)

export const nowIso = (): string => new Date().toISOString()

export const parseDayKey = (key: string): Date => {
    const [year, month, day] = key.split('-').map(Number)

    return new Date(year, (month || 1) - 1, day || 1)
}

export const toDate = (value: string | Date): Date => {
    if (value instanceof Date) return value

    return isDayKey(value) ? parseDayKey(value) : new Date(value)
}

const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime())

export const toDayKey = (value: string | Date): string => {
    const date = toDate(value)

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const todayKey = (): string => toDayKey(new Date())

export const addDays = (key: string, days: number): string => {
    const date = parseDayKey(key)
    date.setDate(date.getDate() + days)

    return toDayKey(date)
}

export const addMonths = (key: string, months: number): string => {
    const date = parseDayKey(key)
    const day = date.getDate()

    date.setDate(1)
    date.setMonth(date.getMonth() + months)

    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
    date.setDate(Math.min(day, lastDay))

    return toDayKey(date)
}

export const diffDays = (from: string, to: string): number => {
    return Math.round((parseDayKey(to).getTime() - parseDayKey(from).getTime()) / DAY_MS)
}

export const formatDate = (value?: string | Date | null): string => {
    if (!value) return '—'

    const date = toDate(value)
    if (!isValidDate(date)) return '—'

    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`
}

export const formatTime = (value?: string | Date | null): string => {
    if (!value) return '—'

    const date = toDate(value)
    if (!isValidDate(date)) return '—'

    const hours = date.getHours()

    return `${hours % 12 || 12}:${pad(date.getMinutes())} ${hours >= 12 ? 'PM' : 'AM'}`
}

export const formatDateTime = (value?: string | Date | null): string => {
    if (!value) return '—'

    return `${formatDate(value)}, ${formatTime(value)}`
}

export const formatClockValue = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number)
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return time

    return `${hours % 12 || 12}:${pad(minutes)} ${hours >= 12 ? 'PM' : 'AM'}`
}

export const formatWeekday = (value: string | Date): string => WEEKDAYS[toDate(value).getDay()] ?? ''

export const toClockValue = (value: string | Date): string => {
    const date = toDate(value)

    return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export const combineDateTime = (dayKey: string, time: string): string => {
    const date = parseDayKey(dayKey)
    const [hours, minutes] = time.split(':').map(Number)

    date.setHours(hours || 0, minutes || 0, 0, 0)

    return date.toISOString()
}

const describeSpan = (days: number): string => {
    if (days < 14) return `${days} days`
    if (days < 60) return `${Math.round(days / 7)} weeks`
    if (days < 365) return `${Math.round(days / 30)} months`

    const years = Math.round(days / 365)

    return years === 1 ? '1 year' : `${years} years`
}

export const relativeDay = (key: string, today: string): string => {
    const diff = diffDays(today, key)

    if (diff === 0) return 'Today'
    if (diff === 1) return 'Tomorrow'
    if (diff === -1) return 'Yesterday'

    const span = describeSpan(Math.abs(diff))

    return diff > 0 ? `In ${span}` : `${span} ago`
}

export const overdueLabel = (key: string, today: string): string => {
    const days = diffDays(key, today)

    return days === 1 ? '1 day overdue' : `${days} days overdue`
}

export const greetingFor = (date: Date): string => {
    const hours = date.getHours()

    if (hours < 12) return 'Good morning'
    if (hours < 17) return 'Good afternoon'

    return 'Good evening'
}

export const getAgeFromDob = (dateOfBirth: string, today: string): number => {
    const birth = parseDayKey(dateOfBirth)
    const now = parseDayKey(today)

    let age = now.getFullYear() - birth.getFullYear()
    const monthDiff = now.getMonth() - birth.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) age -= 1

    return Math.max(0, age)
}

export const isSameDay = (value: string, dayKey: string): boolean => toDayKey(value) === dayKey

export const minutesBetween = (from: string, to: string): number => {
    return Math.abs(new Date(to).getTime() - new Date(from).getTime()) / 60_000
}
